import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { 
  Calculator, 
  ChartLine, 
  ClipboardText, 
  CurrencyDollar, 
  Path,
  Users,
  FileText,
  Handshake,
  Clock,
  Wrench,
  Brain,
  MapPin,
  Bell,
  ChartPie,
  ArrowRight
} from '@phosphor-icons/react';

const FeaturesSection = styled.section`
  background: ${props => props.theme.colors.background || '#ffffff'};
  overflow: hidden;
  position: relative;
`;

const Container = styled.div`
  min-height: 95vh;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4rem;
  padding: 4rem 2rem;
  position: relative;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  flex-direction: ${props => props.reverse ? 'row-reverse' : 'row'};
  
  @media (max-width: 1024px) {
    flex-direction: column;
    min-height: auto;
    gap: 1rem;
    padding: 2rem 1rem;
  }
`;

const TextContent = styled.div`
  width: 35%;
  padding: 0.5rem;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: 1024px) {
    width: 100%;
    text-align: center;
    padding: 0;
    margin-bottom: 0.25rem;
  }
`;

const SubTitle = styled.div`
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${props => props.color || props.theme.colors.primary || '#4f46e5'};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s ease-out;

  .in-view & {
    opacity: 1;
    transform: translateY(0);
  }

  svg {
    font-size: 1.25rem;
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const FeatureTitle = styled.h2`
  font-size: 2.75rem;
  margin-bottom: 1rem;
  opacity: 0;
  transform: translateY(30px);
  filter: blur(10px);
  transition: all 0.8s ease-out;
  color: ${props => props.theme.colors.text || '#000000'};
  line-height: 1.1;

  .in-view & {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 0.75rem;
    line-height: 1.2;
    transform: translateY(15px);
  }
`;

const FeatureDescription = styled.p`
  font-size: 1.15rem;
  line-height: 1.4;
  opacity: 0;
  transform: translateY(30px);
  filter: blur(10px);
  transition: all 0.8s ease-out;
  transition-delay: 0.2s;
  color: ${props => props.theme.colors.text || '#000000'};
  margin: 0;
  max-width: 42ch;

  .in-view & {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.4;
    max-width: 90%;
    margin: 0 auto;
    transform: translateY(15px);
  }
`;

const VisualContent = styled.div`
  width: 65%;
  height: auto;
  position: relative;
  z-index: 1;
  max-width: 1020px;
  aspect-ratio: 16/8;

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
  }
`;

const DemoCard = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  opacity: 0;
  transform: translateY(50px) scale(0.95);
  transition: all 0.8s ease-out;
  transition-delay: 0.4s;

  .in-view & {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const BulletPoints = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease-out;
  transition-delay: 0.3s;

  .in-view & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const BulletPoint = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 1rem;
  color: ${props => props.theme.colors.text || '#374151'};

  svg {
    color: ${props => props.theme.colors.primary || '#4f46e5'};
    flex-shrink: 0;
    font-size: 1rem;
  }

  @media (max-width: 768px) {
    text-align: left;
    font-size: 0.95rem;
  }
`;

const FeatureButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.5rem;
  border-radius: 100px;
  background: ${props => props.theme.colors.primary || '#4f46e5'};
  color: white;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  margin-top: 1.5rem;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease-out;
  transition-delay: 0.4s;
  width: fit-content;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(79, 70, 229, 0.25);
    background: ${props => props.theme.colors.primaryDark || '#4338ca'};
  }

  .in-view & {
    opacity: 1;
    transform: translateY(0);
  }

  svg {
    font-size: 1rem;
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(3px);
  }

  @media (max-width: 768px) {
    width: auto;
    margin: 1.5rem auto 0;
    font-size: 0.95rem;
    padding: 0.75rem 1.25rem;
  }
`;

const MacWrapper = styled.div`
  width: 100%;
  height: 100%;
  background: #1a1a1a;
  border-radius: 16px;
  box-shadow: 
    0 30px 60px -12px rgba(0, 0, 0, 0.25);
  position: relative;
  overflow: hidden;
  aspect-ratio: 16/8;
`;

const MacTopBar = styled.div`
  height: 2rem;
  background: #2a2a2a;
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: center;
  padding: 0 0.875rem;
  margin-bottom: 5px;
`;

const MacButtons = styled.div`
  display: flex;
  gap: 0.5rem;

  &::before,
  &::after,
  & span {
    content: '';
    width: 0.875rem;
    height: 0.875rem;
    border-radius: 50%;
    display: block;
  }

  &::before {
    background: #ff5f57;
  }

  &::after {
    background: #28c840;
  }

  & span {
    background: #febc2e;
  }
`;

const MacScreen = styled.div`
  width: 100%;
  height: calc(100% - 2rem);
  border-radius: 0 0 12px 12px;
  overflow: hidden;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MacImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  image-rendering: crisp-edges;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  will-change: transform;
  perspective: 1000;
  transform-style: preserve-3d;
`;

const features = [
  {
    subtitle: "Industry Leading Platform",
    title: "Field Service Management Software for Everyone",
    description: "Discover why industry leaders are rapidly adopting ServTrackr as their mission-critical platform of choice. With its comprehensive feature set and purpose-built design, ServTrackr is revolutionizing the field service landscape by setting new benchmarks for operational excellence. From real-time inventory tracking and seamless project management to instant margin insights and personalized client portals, ServTrackr empowers businesses to work smarter, faster, and more efficiently.",
    bullets: [
      { text: "Real-time inventory tracking", icon: ClipboardText },
      { text: "Seamless project management", icon: Path },
      { text: "Instant margin insights", icon: ChartLine },
      { text: "Personalized client portals", icon: Users }
    ],
    buttonText: "Learn More",
    buttonLink: "/platform",
    icon: Brain,
    color: "#0891b2",
    video: "/videos/platform-demo.mp4"
  },
  {
    subtitle: "Better FSM Experience",
    title: "Developed with Industry Leaders for a Better FSM Experience",
    description: "ServTrackr simplifies project management by effortlessly tracking timesheets, monitoring job progress, and managing financials. Stay organized and in control with real-time updates and streamlined workflows.",
    bullets: [
      { text: "Timesheet tracking", icon: Clock },
      { text: "Job progress monitoring", icon: ChartPie },
      { text: "Financial management", icon: CurrencyDollar },
      { text: "Real-time updates", icon: Bell }
    ],
    buttonText: "Explore Features",
    buttonLink: "/features",
    icon: Wrench,
    color: "#0d9488",
    video: "/videos/fsm-demo.mp4"
  },
  {
    subtitle: "Financial Control",
    title: "Always Know Your Financials Anytime Anywhere",
    description: "ServTrackr keeps you in command of your inventory and services, meticulously tracking materials and costs to ensure optimal efficiency. With instant insights into your margins, you can make informed decisions and deliver top-tier service anytime, anywhere, all while maintaining complete control over your resources.",
    bullets: [
      { text: "Real-time margin tracking", icon: ChartLine },
      { text: "Inventory management", icon: ClipboardText },
      { text: "Cost optimization", icon: CurrencyDollar },
      { text: "Resource allocation", icon: Path }
    ],
    buttonText: "Explore Financial Features",
    buttonLink: "/features/financials",
    icon: Calculator,
    color: "#4f46e5",
    video: "/videos/financial-demo.mp4"
  },
  {
    subtitle: "Client Experience",
    title: "Deliver a Top-Tier Client Experience",
    description: "ServTrackr creates a personalized client portal for every customer, offering seamless access to invoices, bookings, and estimates. Clients can easily submit new requests and manage recurring events, ensuring a streamlined and professional experience every time.",
    bullets: [
      { text: "Personalized client portals", icon: Users },
      { text: "Easy request submission", icon: FileText },
      { text: "Invoice & estimate access", icon: Handshake },
      { text: "Recurring event management", icon: Clock }
    ],
    buttonText: "View Client Features",
    buttonLink: "/features/client-experience",
    icon: Users,
    color: "#7c3aed",
    video: "/videos/client-demo.mp4"
  },
  {
    subtitle: "Service Management",
    title: "Complete Control Over Your Resources",
    description: "Make informed decisions and deliver top-tier service anytime, anywhere, while maintaining complete control over your resources and operations.",
    bullets: [
      { text: "Smart resource allocation", icon: Brain },
      { text: "Real-time service tracking", icon: MapPin },
      { text: "Automated notifications", icon: Bell },
      { text: "Performance monitoring", icon: ChartPie }
    ],
    buttonText: "Learn About Management",
    buttonLink: "/features/service-management",
    icon: Wrench,
    color: "#06b6d4",
    video: "/videos/service-demo.mp4"
  }
];

const FeaturesAlt = () => {
  const featuresRef = useRef([]);

  useEffect(() => {
    const sections = featuresRef.current;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      {
        root: null,
        rootMargin: '-10%',
        threshold: 0.1
      }
    );

    sections.forEach(section => {
      if (section) observer.observe(section);
    });

    return () => {
      sections.forEach(section => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  return (
    <FeaturesSection>
      {features.map((feature, index) => (
        <Container
          key={index}
          ref={el => featuresRef.current[index] = el}
          reverse={index % 2 !== 0}
          className={index % 2 === 0 ? 'feature-even' : 'feature-odd'}
        >
          <TextContent>
            <SubTitle color={feature.color}>
              {React.createElement(feature.icon)}
              {feature.subtitle}
            </SubTitle>
            <FeatureTitle>
              {feature.title}
            </FeatureTitle>
            <FeatureDescription>
              {feature.description}
            </FeatureDescription>
            <BulletPoints>
              {feature.bullets.map((bullet, index) => (
                <BulletPoint key={index}>
                  {React.createElement(bullet.icon)}
                  {bullet.text}
                </BulletPoint>
              ))}
            </BulletPoints>
            <FeatureButton href={feature.buttonLink}>
              {feature.buttonText}
              <ArrowRight />
            </FeatureButton>
          </TextContent>
          <VisualContent>
            {index === 0 ? (
              <DemoCard>
                <MacWrapper>
                  <MacTopBar>
                    <MacButtons>
                      <span />
                    </MacButtons>
                  </MacTopBar>
                  <MacScreen>
                    <MacImage
                      src="/PD.webp"
                      alt="ServTrackr Platform Dashboard"
                    />
                  </MacScreen>
                </MacWrapper>
              </DemoCard>
            ) : (
              <DemoCard>
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px' }}
                >
                  <source src={feature.video} type="video/mp4" />
                </video>
              </DemoCard>
            )}
          </VisualContent>
        </Container>
      ))}
    </FeaturesSection>
  );
};

export default FeaturesAlt; 